
import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';

export interface FocalPoint {
  x: number; // percentage from left (0-100)
  y: number; // percentage from top (0-100)
}

interface FocalPointSelectorProps {
  imageUrl: string;
  onFocalPointSelected: (focalPoint: FocalPoint) => void;
  onCancel: () => void;
  initialFocalPoint?: FocalPoint;
}

export const FocalPointSelector: React.FC<FocalPointSelectorProps> = ({
  imageUrl,
  onFocalPointSelected,
  onCancel,
  initialFocalPoint = { x: 50, y: 50 },
}) => {
  const [focalPoint, setFocalPoint] = useState<FocalPoint>(initialFocalPoint);
  const imageRef = useRef<HTMLImageElement>(null);

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setFocalPoint({ x, y });
  };
  
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    // Prevent default to avoid scrolling or other touch behaviors
    e.preventDefault();
    
    if (!imageRef.current || !e.touches[0]) return;
    
    const touch = e.touches[0];
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    
    setFocalPoint({ x, y });
  };

  const handleConfirm = () => {
    onFocalPointSelected(focalPoint);
  };

  const aspectRatios = [
    { name: '1:1 (Square)', style: 'aspect-square' },
    { name: '4:5 (Portrait)', style: 'aspect-[4/5]' },
    { name: '16:9 (Landscape)', style: 'aspect-[16/9]' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-lg bg-background border border-border p-4 md:p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Set Focal Point</h3>
          <Button variant="ghost" size="icon" onClick={onCancel} className="h-8 w-8">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </div>
        
        <p className="mb-4 text-sm text-muted-foreground">
          Tap the area of your image you want to keep in focus, regardless of how it's displayed.
        </p>
        
        {/* Main focal point selector */}
        <div 
          className="relative mb-6 overflow-hidden rounded-lg border border-border bg-black/20"
          onClick={handleImageClick}
          onTouchStart={handleTouchStart}
        >
          <img
            ref={imageRef}
            src={imageUrl}
            alt="Select focal point"
            className="h-auto w-full object-contain max-h-[20vh] md:max-h-[25vh]"
          />
          
          {/* Focal point indicator */}
          <div 
            className="absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg"
            style={{ 
              left: `${focalPoint.x}%`, 
              top: `${focalPoint.y}%`,
              backgroundColor: 'rgba(147, 51, 234, 0.5)' // purple with transparency
            }}
          />
        </div>
        
        {/* Preview section to show how it looks in different aspect ratios */}
        <div className="mb-6">
          <h4 className="mb-3 text-sm font-medium text-foreground">Preview in different layouts:</h4>
          <div className="grid grid-cols-3 gap-4">
            {aspectRatios.map((ratio) => (
              <div key={ratio.name} className="space-y-1">
                <p className="text-xs text-muted-foreground text-center">{ratio.name}</p>
                <div 
                  className={`relative overflow-hidden rounded-md bg-black/20 ${ratio.style}`}
                  style={{
                    // Set a consistent container size but let aspect ratio determine dimensions
                    width: '100%',
                    height: ratio.name.includes('Portrait') ? '120px' : 
                           ratio.name.includes('Landscape') ? '60px' : '80px'
                  }}
                >
                  <img
                    src={imageUrl}
                    alt={`Preview in ${ratio.name}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: `${focalPoint.x}% ${focalPoint.y}%` }}
                  />
                  {/* Small indicator of focal point */}
                  <div 
                    className="absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white bg-purple-600 bg-opacity-70 z-10"
                    style={{ 
                      left: `${focalPoint.x}%`, 
                      top: `${focalPoint.y}%` 
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel} variant="outline" size="sm">
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant="default" size="sm">
            Set Focal Point
          </Button>
        </div>
      </div>
    </div>
  );
};
